/**
 * Scan Bills Step 3 — create selected bills and show summary
 */

import React from 'react';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import type { CreateBillsFromScanResult } from '../../../types/bill-scan.types';

interface ScanBillsStep3CreateProps {
  creating: boolean;
  result: CreateBillsFromScanResult | null;
  error?: string | null;
  onClose: () => void;
  onBack: () => void;
}

export function ScanBillsStep3Create({
  creating,
  result,
  error,
  onClose,
  onBack,
}: ScanBillsStep3CreateProps): React.ReactElement {
  if (creating) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2
          className="h-10 w-10 animate-spin text-blue-600"
          aria-label="Creating bills"
        />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Creating your bill reminders…
        </p>
      </div>
    );
  }

  if (error && !result) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Could not create bills
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400 mt-2 max-w-md">{error}</p>
        </div>
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const created = result?.created ?? 0;
  const failed = result?.failed ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center py-8">
        <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {created > 0
            ? `Created ${created} bill${created === 1 ? '' : 's'}`
            : 'No bills created'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md">
          {created > 0
            ? 'Your bill reminders are ready. You can edit them anytime from Bills & Reminders.'
            : 'None of the selected bills could be created.'}
        </p>
      </div>

      {failed.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm"
        >
          <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
            {failed.length} failed to create
          </p>
          <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-0.5">
            {failed.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                {f.name}: {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Done
        </button>
      </div>
    </div>
  );
}
